import { useCallback, useMemo, useState } from 'react'
import { Auth0Client } from '@auth0/auth0-spa-js'
import config from '../config'
import { AuthResponse } from 'types/Auth'

export function useAuth() {
  const [token, setToken] = useState<string | null>(null)

  const auth0Client = useMemo(() => {
    return new Auth0Client({
      domain: config.auth0Domain,
      clientId: config.auth0ClientId,
      authorizationParams: {
        redirect_uri: config.auth0RedirectUri,
        audience: config.auth0Audience,
        scope: config.auth0Scope,
      },
    })
  }, [])

  const loginWithRedirect = useCallback(async () => {
    await auth0Client.loginWithRedirect()
  }, [auth0Client])

  const handleRedirectCallback = useCallback(async (): Promise<AuthResponse> => {
    await auth0Client.handleRedirectCallback()
    const auth0Token = await auth0Client.getTokenSilently()
    setToken(auth0Token)

    const response = await fetch(`${config.api}/authenticate/token`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${auth0Token}`,
      },
    })

    const data = await response.json()

    if (response.ok) {
      return {
        sessionData: data,
      }
    }

    const user = await auth0Client.getUser()

    return {
      errorCode: data._code,
      sessionData: null,
      email: user?.email || '',
    }
  }, [auth0Client])

  const linkUserToSso = useCallback(
    async (email: string, password: string): Promise<AuthResponse> => {
      if (!token) {
        throw new Error('No token available to link user to SSO')
      }
      const response = await fetch(`${config.api}/authenticate/linksso`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          password,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        return {
          sessionData: data,
        }
      }

      const errorData = await response.json()
      return {
        errorCode: errorData._code,
        sessionData: null,
      }
    },
    [token]
  )

  return { loginWithRedirect, handleRedirectCallback, linkUserToSso }
}
